import { useState } from "react";
import DateRangeInput from "src/components/common/DateRangeInput";
import { useBarcodeScan } from "src/hooks/useBarcodeScan";
import { PDA1050_R01Filter } from "./PDA1050_R01Dto";
import usePDA1050_R01Store from "./Store_PDA1050_R01";

interface PDA1050_R01SearchProps {
  onSearch: (condition?: PDA1050_R01Filter) => Promise<void> | void;
}

/**
 * 발주번호와 기간으로 입고 대상 발주를 검색하는 목록 화면의 검색 영역이다.
 * 발주번호는 하드웨어 스캔과 모바일 키보드 입력을 모두 지원한다.
 */
const PDA1050_R01Search = ({ onSearch }: PDA1050_R01SearchProps) => {
  const { PDA1050_R01Filter, setPDA1050_R01Filter } =
    usePDA1050_R01Store();
  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);

  /**
   * 확정된 발주번호 스캔값을 현재 기간 조건과 합쳐 즉시 목록을 조회한다.
   * 비동기 Store 반영 전에 최신 발주번호가 누락되지 않도록 완성된 조건 객체를 onSearch에 직접 전달한다.
   *
   * @param purchaseOrderNo 스캐너 또는 키보드로 입력한 발주번호
   */
  const handlePurchaseOrderScan = (purchaseOrderNo: string) => {
    const nextCondition = {
      ...PDA1050_R01Filter,
      purchaseOrderNo,
    };
    setPDA1050_R01Filter({ purchaseOrderNo });
    void onSearch(nextCondition);
  };

  const { scanInputRef, handleScanKeyDown, handleInput } = useBarcodeScan(
    handlePurchaseOrderScan,
    {
      clearOnScan: false,
      refocusOnScan: true,
      debounceMs: 100,
      autoFocus: true,
    }
  );

  /** 300ms 이내 더블 탭 시 소프트 키보드 입력 모드를 활성화한다. */
  const handleDoubleTap = () => {
    const currentTime = Date.now();
    if (currentTime - lastTapTime < 300) {
      setKeyboardEnabled(true);
      scanInputRef.current?.focus();
    }
    setLastTapTime(currentTime);
  };

  /** 키보드 입력 모드를 전환하고, 활성화 시 발주번호 입력칸에 포커스를 준다. */
  const toggleKeyboard = () => {
    setKeyboardEnabled((isEnabled) => !isEnabled);
    if (!keyboardEnabled) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  };

  return (
    <div className="w-full bg-white p-4 pb-1 border-b border-gray-200">
      <div className="space-y-3">
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">
            발주번호
          </label>
          <div className="flex-1 flex gap-1">
            <input
              ref={scanInputRef}
              type="text"
              value={PDA1050_R01Filter.purchaseOrderNo}
              onChange={(event) =>
                setPDA1050_R01Filter({
                  purchaseOrderNo: event.target.value,
                })
              }
              onInput={handleInput}
              onKeyDown={handleScanKeyDown}
              onClick={handleDoubleTap}
              inputMode={keyboardEnabled ? "text" : "none"}
              autoComplete="off"
              className="flex-1 max-w-[215px] px-2 py-0.5 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="발주번호 스캔"
            />
            <button
              type="button"
              onClick={toggleKeyboard}
              className={`w-10 flex-shrink-0 px-2 py-0.5 border border-gray-300 rounded transition-colors ${
                keyboardEnabled
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              title={keyboardEnabled ? "키보드 숨기기" : "키보드 표시"}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </button>
          </div>
        </div>

        <DateRangeInput
          startDate={PDA1050_R01Filter.startDate}
          endDate={PDA1050_R01Filter.endDate}
          onStartDateChange={(startDate) =>
            setPDA1050_R01Filter({ startDate })
          }
          onEndDateChange={(endDate) => setPDA1050_R01Filter({ endDate })}
          label="입고일자"
          labelWidth="w-20"
        />

        <button
          type="button"
          onClick={() => void onSearch()}
          className="w-full bg-blue-500 text-white px-4 py-2 rounded text-lg font-medium hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          조회
        </button>
      </div>
    </div>
  );
};

export default PDA1050_R01Search;
